import React, { useEffect, useMemo, useState } from 'react';

const ChineseZIPCodeInput = ({
  value = '',
  onChange,
  label = '邮政编码',
  placeholder,
  required = false,
  className = '',
  maxLength,
  disabled = false,
  countryCode = 'CHN',
}) => {
  const [error, setError] = useState('');

  const normalizedCountry = useMemo(() => (countryCode || '').toUpperCase(), [countryCode]);
  const isChina =
    !normalizedCountry ||
    ['CHN', 'CHIN', 'CN', 'MAC', 'HKG', 'TWN'].includes(normalizedCountry);
  const effectiveMaxLength = useMemo(() => {
    if (typeof maxLength === 'number') return maxLength;
    return isChina ? 6 : 12;
  }, [isChina, maxLength]);

  const computedPlaceholder = useMemo(() => {
    if (placeholder !== undefined && placeholder !== null) {
      return placeholder;
    }
    return isChina ? '610000' : 'Postal / ZIP code';
  }, [placeholder, isChina]);

  const validateZIPCode = (zipCode) => {
    if (!zipCode) {
      return '';
    }

    if (isChina) {
      return /^\d{6}$/.test(zipCode)
        ? ''
        : '请输入有效的中国邮政编码格式（6位数字，如：610000）';
    }

    if (zipCode.length < 3) {
      return '邮政编码至少需要3个字符';
    }

    return /^[A-Za-z0-9\s-]{3,20}$/.test(zipCode)
      ? ''
      : '邮政编码仅支持字母、数字、空格和连字符';
  };

  const handleChange = (event) => {
    let inputValue = event.target.value || '';

    if (isChina) {
      inputValue = inputValue.replace(/[^0-9]/g, '');
    } else {
      inputValue = inputValue.replace(/[^A-Za-z0-9\s-]/g, '');
    }

    if (inputValue.length > effectiveMaxLength) {
      inputValue = inputValue.slice(0, effectiveMaxLength);
    }

    const validationError = validateZIPCode(inputValue);
    setError(validationError);
    onChange(inputValue);
  };

  const handleBlur = () => {
    if (!value) {
      setError(required ? '请输入邮政编码' : '');
      return;
    }

    const validationError = validateZIPCode(value);
    setError(validationError);
  };

  useEffect(() => {
    if (!value) {
      setError('');
      return;
    }

    setError(validateZIPCode(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedCountry]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={computedPlaceholder}
          maxLength={effectiveMaxLength}
          disabled={disabled}
          className={`w-full h-12 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
            error 
              ? 'border-red-300 bg-red-50 focus:ring-red-500' 
              : 'border-gray-300 bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md'
          } ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
        />
        
        {/* 格式提示 */}
        {!error && value && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-green-500">
            ✓
          </div>
        )}
      </div>
      
      {/* 错误信息 */}
      {error && (
        <p className="mt-1 text-sm text-red-600 flex items-center">
          <span className="w-4 h-4 mr-1">⚠️</span>
          {error}
        </p>
      )}
      
      {/* 帮助信息 */}
      {!error && !value && !required && (
        <p className="mt-1 text-xs text-gray-500">
          {isChina ? '格式：6位数字，如：610000' : '格式示例：SW1A 1AA、12345、H0H 0H0'}
        </p>
      )}
    </div>
  );
};

export default ChineseZIPCodeInput; 
